import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MenuItem, LocalCategoryModel } from '../models/index';
import ImagePopup from './ImagePopup';
import ItemCard from './ItemCard';
import { LeafMark, SprigDivider } from './Ornaments';
import '../styles.css';

// Translation Object
const translations = {
  en: {
    backToCategories: "← Back to Categories",
    subcategories: "Subcategories",
    all: "All",
  },
  gr: {
    backToCategories: "← Πίσω στις Κατηγορίες",
    subcategories: "Υποκατηγορίες",
    all: "Όλα",
  },
};

const withoutLang = (id: string) => id.replace(/^(gr|en)_/, '');

/**
 * The category named in the URL, so refreshed and shared links work. A link to the other
 * language's twin (gr_x / en_x) opens the same category in the current language.
 */
function findCategory(categories: LocalCategoryModel[], id: string | undefined): LocalCategoryModel | null {
  if (!id) return null;
  return (
    categories.find((c) => c.categoryID === id) ??
    categories.find((c) => withoutLang(c.categoryID) === withoutLang(id)) ??
    null
  );
}

interface ItemsPageProps {
  setLogoVisible: (visible: boolean) => void;
  selectedLanguage: 'en' | 'gr';
  categories: LocalCategoryModel[];
  /** The menu has not arrived yet. */
  loading: boolean;
}

const ItemsPage: React.FC<ItemsPageProps> = ({ setLogoVisible, selectedLanguage, categories, loading }) => {
  const { categoryID } = useParams();
  const selectedCategory = findCategory(categories, categoryID);
  const navigate = useNavigate();
  const subcategoriesRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null); // Reference to the header section
  const [isSticky, setIsSticky] = useState(false);
  const [initialOffset, setInitialOffset] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [stickyTop, setStickyTop] = useState(0); // New state for sticky top position
  
  // State for managing the popup
  const [popupItem, setPopupItem] = useState<MenuItem | null>(null);

  useEffect(() => {
    setLogoVisible(false);
    return () => setLogoVisible(true); // Show the logo when the component unmounts
  }, [setLogoVisible]);

  // Check if device is mobile on mount and window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Capture the bar's initial offset once the category is on screen (after the spinner on a
  // deep link)
  const selectedID = selectedCategory?.categoryID;
  useEffect(() => {
    if (subcategoriesRef.current) {
      setInitialOffset(subcategoriesRef.current.getBoundingClientRect().top + window.scrollY);
    }
  }, [selectedID]);

  useEffect(() => {
    if (initialOffset === null) return;

    const handleScroll = () => {
      // Calculate appropriate top position for mobile
      if (isMobile && headerRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        setStickyTop(headerHeight > 0 ? headerHeight / 3 : 0);
      } else {
        setStickyTop(0);
      }

      setIsSticky(window.scrollY >= initialOffset);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [initialOffset, isMobile]);

  const scrollToSubcategory = useCallback((subcategoryId: string) => {
    const element = document.getElementById(subcategoryId);
    if (element && subcategoriesRef.current && contentRef.current) {
      const stickyHeight = subcategoriesRef.current.offsetHeight || 0;
      const contentTop = contentRef.current.offsetTop || 0;

      // Adjust scroll position based on whether we're on mobile
      const adjustedOffset = isMobile && subcategoriesRef.current ? subcategoriesRef.current.offsetHeight + stickyTop : 0;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - contentTop;
      const offsetPosition = elementPosition - stickyHeight - 20 - adjustedOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

    } else {
    }
  }, [isMobile, stickyTop]);

  if (!selectedCategory) {
    if (loading) {
      return (
        <div className="spinner-container" role="status" aria-label={selectedLanguage === 'en' ? 'Loading' : 'Φόρτωση'}>
          <div className="loading-spinner" />
        </div>
      );
    }
    // Unknown category: back to the categories page
    return <Navigate to="/categories" replace />;
  }

  // Use selectedLanguage for translations
  const t = translations[selectedLanguage];

  // Utility function to handle <br> tags and newline characters in text
  const renderTextWithLineBreaks = (text: string) => {
    // Split by both <br> tags and newline characters (\n)
    const textParts = text.split(/(<br>|\n)/g);
    return textParts.map((part, index) => {
      if (part === '<br>' || part === '\n') {
        return <br key={index} />;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  return (
    <div className="items-page-container">
      {/* Back Arrow Section */}
      <div className="back-arrow-container" ref={headerRef}>
        <button
          className="back-arrow-button"
          onClick={() => navigate('/categories', { replace: true })}
        >
          {t.backToCategories}
        </button>
      </div>

      {/* Items Page Content */}
      <div className="items-page" ref={contentRef}>
        <header className="menu-section-head">
          <span className="menu-eyebrow">
            <LeafMark className="menu-eyebrow__mark" />
            {selectedLanguage === 'en' ? 'Our Menu' : 'ΤΟ ΜΕΝΟΥ ΜΑΣ'}
          </span>
          <h2 className="category-title">{selectedCategory.categoryName}</h2>
          <SprigDivider className="menu-divider" />
        </header>

        {selectedCategory.categoryDescription && (
          <p className="category-description-itemPage">
            {renderTextWithLineBreaks(selectedCategory.categoryDescription)}
          </p>
        )}

        {selectedCategory.subcategories.length > 1 && (
          <div
            className={`sticky-subcategories ${isSticky ? 'sticky' : ''}`}
            ref={subcategoriesRef}
          >
            <div className="subcategory-scroll">
              {selectedCategory.subcategories.map((subcategory) => (
                <button
                  key={subcategory.subcategoryID}
                  onClick={() => scrollToSubcategory(subcategory.subcategoryID.toString())}
                  className="subcategory-button"
                >
                  {subcategory.subcategoryName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Display Subcategories and their items */}
        <div className={`subcategories-content ${isSticky ? 'sticky-offset' : ''}`}>
          {selectedCategory.subcategories.length > 0 ? (
            selectedCategory.subcategories.map((subcategory) => (
              <div key={subcategory.subcategoryID} id={subcategory.subcategoryID.toString()} className="subcategory-section">
                {selectedCategory.subcategories.length > 1 && (
                  <h3 className="subcategory-title">
                    <LeafMark className="subcategory-title__mark" />
                    {subcategory.subcategoryName}
                  </h3>
                )}
                <div className="items">
                  {subcategory.items
                    .filter((item: MenuItem) => item.available)
                    .map((item: MenuItem) => (
                      <ItemCard
                        key={item.itemID}
                        item={{
                          ...item,
                          img: item.img || undefined,
                          itemOrder: String(item.itemOrder),
                          itemTitle: item.itemTitle || item.itemName,
                        }}
                        language={selectedLanguage}
                        onImageClick={() => setPopupItem(item)}
                        title={item.itemTitle || item.itemName}
                        description={item.itemDescription || ''}
                        price={item.price}
                      />
                    ))}
                </div>
              </div>
            ))
          ) : (
            <div className="items">
              {selectedCategory.items
                .filter((item: MenuItem) => item.available)
                .map((item: MenuItem) => (
                  <ItemCard
                    key={item.itemID}
                    item={{
                      ...item,
                      img: item.img || undefined,
                      itemOrder: String(item.itemOrder),
                      itemTitle: item.itemTitle || item.itemName
                    }}
                    language={selectedLanguage}
                    onImageClick={() => setPopupItem(item)}
                    title={item.itemTitle || item.itemName}
                    description={item.itemDescription || ''}
                    price={item.price}
                  />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Popup */}
      {popupItem && (
        <ImagePopup
          item={{
            ...popupItem,
            img: popupItem.img || undefined,
            itemOrder: String(popupItem.itemOrder),
            itemTitle: popupItem.itemTitle || popupItem.itemName,
            itemDescription: popupItem.itemDescription || '',
          }}
          onClose={() => setPopupItem(null)}
        />
      )}
    </div>
  );
};

export default ItemsPage;